import { Banner, InlineStack, Spinner, Text } from '@shopify/polaris';
import { useSyncStatus, useTriggerSync } from '../../hooks/useSyncStatus';

/**
 * Shown on the dashboard when store data has never been synced or a sync failed.
 * Polls every 5 s while sync is in progress.
 */
export function SyncStatusBanner() {
  const { data, isLoading } = useSyncStatus();
  const { mutate: trigger, isPending } = useTriggerSync();

  if (isLoading || !data) return null;

  const resources  = Object.values(data.resources);
  const neverSynced = resources.some((r) => r.status === 'never');
  const hasFailed   = resources.some((r) => r.status === 'failed');
  const isRunning   = resources.some((r) => r.status === 'running');

  if (!neverSynced && !hasFailed && !isRunning) return null;

  if (isRunning) {
    return (
      <Banner tone="info" title="Syncing store data…">
        <InlineStack gap="200" align="center">
          <Spinner size="small" accessibilityLabel="Syncing" />
          <Text as="p">
            Your store data is being imported. This may take a minute.
          </Text>
        </InlineStack>
      </Banner>
    );
  }

  return (
    <Banner
      tone={hasFailed ? 'warning' : 'info'}
      title={
        hasFailed
          ? 'Some store data failed to sync'
          : 'Store data not yet synced'
      }
      action={{
        content:    isPending ? 'Queuing…' : 'Sync now',
        onAction:   () => trigger(true),
        disabled:   isPending,
      }}
    >
      <Text as="p">
        {hasFailed
          ? 'An error occurred during the last sync. Click "Sync now" to retry.'
          : 'Import your store data to enable audits and AI recommendations.'}
      </Text>
    </Banner>
  );
}
